import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  DocumentDuplicateIcon,
  PhotoIcon,
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon
} from '@heroicons/react/24/outline';
import { useCourses } from '../../contexts/CourseContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { uploadImageToStorage } from '../../utils/imageUpload';

interface CourseFormData {
  courseName: string; // 필수
  courseDescription: string; // 필수
  instructor: string; // 필수
  courseImage?: string;
  category?: ('group' | 'preschool' | 'elementary' | 'secondary' | 'adult' | 'special' | 'camp' | 'international')[];
  is_published: boolean;
  // 수강료 정보 (표시용 텍스트)
  tuition: string;
  materialFee: string;
  // 문의 정보
  contactPerson: string;
  contactPhone: string;
  // 모집 방법
  selectionMethod: 'first-come' | 'lottery';
  // 운영일정 (텍스트)
  schedule: string;
  // 상태 관리 (2025-01-27 추가)
  status: 'upcoming' | 'recruiting' | 'ongoing' | 'completed';
  status_mode: 'auto' | 'manual';
  // 노출 제어 (2025-01-27 추가)
  show_in_catalog: boolean;
  show_in_registration: boolean;
  catalog_order?: number;
  registration_order?: number;
}

const CourseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addCourse, updateCourse, getCourse } = useCourses();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<CourseFormData>({
    courseName: '',
    courseDescription: '',
    instructor: '',
    courseImage: '',
    category: [],
    is_published: true,
    // 수강료 정보 기본값 (표시용 텍스트)
    tuition: '',
    materialFee: '',
    // 문의 정보 기본값
    contactPerson: '동두천시 글로벌인재교육센터',
    contactPhone: '031-863-3522',
    // 모집 방법 기본값
    selectionMethod: 'first-come',
    // 운영일정 기본값
    schedule: '',
    // 상태 관리 기본값 (2025-01-27 추가)
    status: 'upcoming',
    status_mode: 'auto',
    // 노출 제어 기본값 (2025-01-27 추가)
    show_in_catalog: false,
    show_in_registration: false,
    catalog_order: undefined,
    registration_order: undefined
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CourseFormData, string>>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorContentLoaded = useRef(false);

  // TipTap Editor for courseDescription
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      Placeholder.configure({
        placeholder: '교육 내용을 상세히 입력해주세요...',
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setFormData(prev => ({ ...prev, courseDescription: html }));
      if (errors.courseDescription) {
        setErrors(prev => ({ ...prev, courseDescription: '' }));
      }
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name as keyof CourseFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleCourseImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const imageUrl = await uploadImageToStorage(file, 'courses');
        setFormData({ ...formData, courseImage: imageUrl });
        console.log('Course image uploaded successfully:', imageUrl);
      } catch (error) {
        console.error('Failed to upload course image:', error);
        alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const handleDescriptionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editor) {
      try {
        const imageUrl = await uploadImageToStorage(file, 'description-images');
        // 중앙 정렬된 이미지 삽입 - div로 감싸기
        editor.chain().focus().insertContent(`<div style="text-align: center; margin: 2rem 0;"><img src="${imageUrl}" style="max-width: 100%; height: auto; display: inline-block;" /></div>`).run();
        console.log('Description image uploaded successfully:', imageUrl);
      } catch (error) {
        console.error('Failed to upload description image:', error);
        alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const addImageToDescription = () => {
    fileInputRef.current?.click();
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof CourseFormData, string>> = {};

    // 필수 필드 검증
    if (!formData.courseName.trim()) newErrors.courseName = '강좌명은 필수입니다.';
    if (!formData.courseDescription || formData.courseDescription === '<p></p>') {
      newErrors.courseDescription = '교육내용은 필수입니다.';
    }
    if (!formData.instructor.trim()) newErrors.instructor = '강사명은 필수입니다.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const editorHtml = editor?.getHTML() || formData.courseDescription;

      const courseData = {
        ...formData,
        courseDescription: editorHtml,
        // 기본값 설정 (반 생성 시 필요할 수 있는 필드들)
        maxStudents: 20,
        waitingList: 5,
        registrationStartDate: '',
        registrationEndDate: '',
        registrationStartTime: '09:00',
        registrationEndTime: '18:00',
        courseStartDate: '',
        courseEndDate: '',
        courseCycle: 'weekly' as const,
        courseDays: [] as string[],
        courseTime: '',
        courseStartTime: '10:00',
        courseEndTime: '12:00',
        totalSessions: 0,
        institution: '동두천시 글로벌인재교육센터',
        location: '',
        contactPerson: formData.contactPerson,
        contactPhone: formData.contactPhone,
        selectionMethod: formData.selectionMethod,
        schedule: formData.schedule
        // status와 status_mode는 formData에서 그대로 사용 (하드코딩 제거)
      };

      if (isEditMode && id) {
        updateCourse(id, courseData);
        alert('강의가 성공적으로 수정되었습니다!');
      } else {
        addCourse(courseData);
        alert('강의가 성공적으로 등록되었습니다!\n이제 강의 상세 페이지에서 반을 추가해주세요.');
      }
      navigate('/admin/courses');
    }
  };

  // 템플릿 데이터 적용
  const applyTemplate = () => {
    const templateData: CourseFormData = {
      courseName: '초급 영어회화 - 일상생활 편',
      courseDescription: '<p>일상생활에서 자주 사용되는 기초 영어회화를 학습합니다.</p><p>인사, 쇼핑, 길찾기, 식당 주문 등 실생활에 필요한 표현들을 중심으로 진행됩니다.</p><p>초보자도 쉽게 따라올 수 있는 수준으로 구성되었습니다.</p>',
      instructor: '제임스 리',
      courseImage: '',
      category: ['adult'],
      is_published: true,
      tuition: '',
      materialFee: '',
      contactPerson: '동두천시 글로벌인재교육센터',
      contactPhone: '031-863-3522',
      selectionMethod: 'first-come',
      schedule: '2025.03 ~ 2025.06 (매주 월, 수)',
      status: 'upcoming',
      status_mode: 'auto',
      show_in_catalog: false,
      show_in_registration: false,
      catalog_order: undefined,
      registration_order: undefined
    };
    setFormData(templateData);
    if (editor) {
      editor.commands.setContent(templateData.courseDescription);
    }
    alert('템플릿이 적용되었습니다. 필요한 부분을 수정해주세요.');
  };

  // Edit mode: Load existing course data
  useEffect(() => {
    if (isEditMode && id) {
      const course = getCourse(id);
      if (course) {
        setFormData({
          courseName: course.courseName,
          courseDescription: course.courseDescription,
          instructor: course.instructor,
          courseImage: course.courseImage || '',
          category: course.category,
          is_published: course.is_published,
          // 수강료 정보 (표시용 텍스트)
          tuition: course.tuition || '',
          materialFee: course.materialFee || '',
          // 문의 정보
          contactPerson: course.contactPerson || '동두천시 글로벌인재교육센터',
          contactPhone: course.contactPhone || '031-863-3522',
          // 모집 방법
          selectionMethod: course.selectionMethod || 'first-come',
          // 운영일정
          schedule: course.schedule || '',
          // 상태 관리 필드 (2025-01-27 추가)
          status: course.status,
          status_mode: course.status_mode || 'auto',
          // 노출 제어 필드 (2025-01-27 추가)
          show_in_catalog: course.show_in_catalog ?? false,
          show_in_registration: course.show_in_registration ?? false,
          catalog_order: course.catalog_order,
          registration_order: course.registration_order
        });
      }
    }
  }, [isEditMode, id, getCourse]);

  // 일반 텍스트를 HTML로 변환
  const convertTextToHtml = (text: string): string => {
    if (text.includes('<p>') || text.includes('<br>') || text.includes('<div>')) {
      return text;
    }
    const paragraphs = text.split('\n').filter(line => line.trim() !== '');
    if (paragraphs.length === 0) return text;
    return paragraphs.map(p => `<p>${p}</p>`).join('');
  };

  // 에디터에 기존 내용 로드
  useEffect(() => {
    if (editor && isEditMode && formData.courseDescription && !editorContentLoaded.current) {
      const htmlContent = convertTextToHtml(formData.courseDescription);
      editor.commands.setContent(htmlContent);
      editorContentLoaded.current = true;
    }
  }, [editor, isEditMode, formData.courseDescription]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {isEditMode ? '강의 수정' : '강의 등록'}
            </h1>
            <p className="text-gray-600">
              {isEditMode
                ? '강의 메타 정보를 수정합니다. 반 관리는 강의 상세 페이지에서 진행해주세요.'
                : '새로운 강의를 등록합니다. 등록 후 강의 상세 페이지에서 반을 추가해주세요.'}
            </p>
            <p className="text-gray-600 mt-1">
              <span className="text-red-500">*</span> 표시는 필수 항목입니다.
            </p>
          </div>
          {!isEditMode && (
            <button
              type="button"
              onClick={applyTemplate}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
              템플릿 적용
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">강의 기본 정보</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                강좌명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="courseName"
                value={formData.courseName}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.courseName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="예: 비즈니스 영어회화"
              />
              {errors.courseName && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.courseName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                강사명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="instructor"
                value={formData.instructor}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.instructor ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="예: 홍길동"
              />
              {errors.instructor && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.instructor}
                </p>
              )}
            </div>

            {/* 수강료 정보 (표시용 텍스트) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  수강료
                </label>
                <input
                  type="text"
                  name="tuition"
                  value={formData.tuition}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="예: 50,000원, 무료, 문의"
                />
                <p className="mt-1 text-xs text-gray-500">프로그램 상세 페이지에 표시될 수강료 정보</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  재료비
                </label>
                <input
                  type="text"
                  name="materialFee"
                  value={formData.materialFee}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="예: 10,000원, 별도, 없음"
                />
                <p className="mt-1 text-xs text-gray-500">교재비, 재료비 등 별도 비용 정보</p>
              </div>
            </div>

            {/* 문의 정보 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  문의처
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="예: 동두천시 글로벌인재교육센터"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  문의 전화번호
                </label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="예: 031-863-3522"
                />
              </div>
            </div>

            {/* 모집 방법 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                모집 방법
              </label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="selectionMethod"
                    value="first-come"
                    checked={formData.selectionMethod === 'first-come'}
                    onChange={(e) => setFormData({ ...formData, selectionMethod: e.target.value as 'first-come' | 'lottery' })}
                    className="mr-2 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">선착순</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="selectionMethod"
                    value="lottery"
                    checked={formData.selectionMethod === 'lottery'}
                    onChange={(e) => setFormData({ ...formData, selectionMethod: e.target.value as 'first-come' | 'lottery' })}
                    className="mr-2 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">추첨</span>
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">반 추가 시 이 설정이 기본값으로 적용됩니다</p>
            </div>

            {/* 운영일정 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                운영일정
              </label>
              <input
                type="text"
                name="schedule"
                value={formData.schedule}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="예: 2025.03 ~ 2025.06 (매주 월, 수)"
              />
              <p className="mt-1 text-xs text-gray-500">프로그램 상세 페이지에 표시될 운영일정입니다</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                프로그램 유형 (중복 선택 가능)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { value: 'group', label: '단체체험' },
                  { value: 'preschool', label: '유치과정' },
                  { value: 'elementary', label: '초등과정' },
                  { value: 'secondary', label: '중·고등과정' },
                  { value: 'adult', label: '성인과정' },
                  { value: 'special', label: '특별과정' },
                  { value: 'camp', label: '방학캠프' },
                  { value: 'international', label: '국제교류' }
                ].map((option) => (
                  <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.category?.includes(option.value as any) || false}
                      onChange={(e) => {
                        const newCategories = e.target.checked
                          ? [...(formData.category || []), option.value as any]
                          : (formData.category || []).filter(c => c !== option.value);
                        setFormData({ ...formData, category: newCategories });
                      }}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-5 w-5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">공개</span>
                  <p className="text-xs text-gray-500">체크하면 사용자 사이트에 공개됩니다. 체크 해제하면 관리자만 볼 수 있습니다.</p>
                </div>
              </label>
            </div>

            {/* 상태 관리 섹션 (2025-01-27 추가) */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">프로그램 상태 관리</h3>
              <p className="text-xs text-gray-600 mb-4">
                프로그램 상태를 자동 또는 수동으로 관리할 수 있습니다.
              </p>

              <div className="space-y-4">
                {/* 상태 관리 모드 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    상태 관리 방식
                  </label>
                  <select
                    value={formData.status_mode}
                    onChange={(e) => setFormData({ ...formData, status_mode: e.target.value as 'auto' | 'manual' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="auto">자동 (날짜 기반)</option>
                    <option value="manual">수동 설정</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.status_mode === 'auto'
                      ? '모집 기간과 교육 기간에 따라 상태가 자동으로 변경됩니다.'
                      : '상태를 직접 선택할 수 있습니다.'}
                  </p>
                </div>

                {/* 수동 상태 선택 */}
                {formData.status_mode === 'manual' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      프로그램 상태
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="upcoming">모집예정</option>
                      <option value="recruiting">모집중</option>
                      <option value="ongoing">진행중</option>
                      <option value="completed">종료</option>
                    </select>
                  </div>
                )}

                {/* 자동 모드일 때 현재 상태 안내 */}
                {formData.status_mode === 'auto' && (
                  <div className="bg-white border border-amber-200 rounded p-3">
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold">현재 설정값:</span> {formData.status === 'upcoming' ? '모집예정' : formData.status === 'recruiting' ? '모집중' : formData.status === 'ongoing' ? '진행중' : '종료'}
                      <br />
                      <span className="text-amber-600">※ 자동 모드에서는 실제 표시 상태가 날짜에 따라 다를 수 있습니다.</span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* 노출 제어 섹션 (2025-01-27 추가) */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">프로그램 노출 설정</h3>
              <p className="text-xs text-gray-600 mb-4">
                프로그램을 어디에 표시할지 선택하세요.
                "프로그램 카탈로그"는 소개용, "수강신청"은 실제 모집용입니다.
              </p>

              <div className="space-y-4">
                {/* 프로그램 카탈로그 노출 */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={formData.show_in_catalog}
                      onChange={(e) => setFormData({ ...formData, show_in_catalog: e.target.checked })}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-5 w-5 mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-700">프로그램 카탈로그에 표시</span>
                      <p className="text-xs text-gray-500">프로그램 소개 페이지(/programs)에 노출됩니다</p>
                    </div>
                  </label>
                  {formData.show_in_catalog && (
                    <div className="ml-8">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        표시 순서 (낮을수록 위에 표시)
                      </label>
                      <input
                        type="number"
                        value={formData.catalog_order ?? ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          catalog_order: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        placeholder="예: 1, 2, 3..."
                        className="w-32 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  )}
                </div>

                {/* 수강신청 노출 */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={formData.show_in_registration}
                      onChange={(e) => setFormData({ ...formData, show_in_registration: e.target.checked })}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-5 w-5 mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-700">수강신청에 표시</span>
                      <p className="text-xs text-gray-500">수강신청 페이지(/application)에 노출됩니다</p>
                    </div>
                  </label>
                  {formData.show_in_registration && (
                    <div className="ml-8">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        표시 순서 (낮을수록 위에 표시)
                      </label>
                      <input
                        type="number"
                        value={formData.registration_order ?? ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          registration_order: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        placeholder="예: 1, 2, 3..."
                        className="w-32 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                강의 이미지
              </label>
              {formData.courseImage && (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">현재 이미지:</p>
                  <img
                    src={formData.courseImage}
                    alt="강의 이미지 미리보기"
                    className="w-full max-w-md h-64 object-cover rounded-lg border border-gray-300"
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleCourseImageUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.courseImage ? '새 이미지를 업로드하려면 파일을 선택하세요' : '이미지 파일을 선택하세요'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육내용 <span className="text-red-500">*</span>
              </label>
              <div className={`border rounded-lg ${errors.courseDescription ? 'border-red-500' : 'border-gray-300'} bg-white`}>
                {/* 에디터 툴바 */}
                <div className="flex items-center gap-0.5 p-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bold') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bold"
                  >
                    <BoldIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('italic') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Italic"
                  >
                    <ItalicIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 2"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 3"
                  >
                    H3
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bulletList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bullet List"
                  >
                    <ListBulletIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('orderedList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Numbered List"
                  >
                    <NumberedListIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={addImageToDescription}
                    className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
                    title="Insert Image"
                  >
                    <PhotoIcon className="h-5 w-5" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleDescriptionImageUpload}
                    className="hidden"
                  />

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* 색상 버튼들 */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!editor) return;
                      const { from, to } = editor.state.selection;
                      const text = editor.state.doc.textBetween(from, to, '');
                      if (text) {
                        editor.chain().focus().deleteSelection().insertContent(`<span style="color: #000000">${text}</span>`).run();
                      }
                    }}
                    className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                    style={{ backgroundColor: '#000000' }}
                    title="검정색"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!editor) return;
                      const { from, to } = editor.state.selection;
                      const text = editor.state.doc.textBetween(from, to, '');
                      if (text) {
                        editor.chain().focus().deleteSelection().insertContent(`<span style="color: #059669">${text}</span>`).run();
                      }
                    }}
                    className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                    style={{ backgroundColor: '#059669' }}
                    title="에메랄드"
                  />
                </div>
                {/* 에디터 본문 */}
                <EditorContent
                  editor={editor}
                  className="prose max-w-none p-4 min-h-[300px] focus:outline-none"
                />
              </div>
              {errors.courseDescription && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.courseDescription}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 안내 메시지 */}
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">📝 강의 등록 안내</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• 강의는 프로그램의 메타 정보만 포함합니다 (강좌명, 강사, 교육내용, 이미지 등)</li>
            <li>• 실제 모집 정보(정원, 모집기간, 수강료 등)는 <strong>반(Class)</strong>에서 관리됩니다</li>
            <li>• 강의 등록 후 강의 상세 페이지에서 <strong>"반 추가"</strong> 버튼을 눌러 반을 생성해주세요</li>
            <li>• 하나의 강의에 여러 개의 반을 개설할 수 있습니다</li>
          </ul>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/courses')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            {isEditMode ? '수정 완료' : '강의 등록'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CourseForm;
